// landing/src/admin/pages/AdminAccounts.js
/**
 * Admin Dashboard for managing multiple WhatsApp accounts
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import AdminLayout from '../components/AdminLayout';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Power, PowerOff, X, QrCode, Zap, CheckCircle, AlertCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { adminAPI } from '../../utils/api';

const AdminAccounts = () => {
    const navigate = useNavigate();
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const user = useAuthStore((state) => state.user);
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    const [accounts, setAccounts] = useState([]);
    const [capacity, setCapacity] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [sessionId, setSessionId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [qrCode, setQrCode] = useState(null);
    const [qrSessionId, setQrSessionId] = useState(null);
    const [qrLoading, setQrLoading] = useState(false);
    const [reconnectingAccountId, setReconnectingAccountId] = useState(null);
    const [deletingAccountId, setDeletingAccountId] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmSessionId, setDeleteConfirmSessionId] = useState(null);
    const [accountsTab, setAccountsTab] = useState('all'); // 'all', 'admin', 'user'

    // Auth check
    useEffect(() => {
        if (!isAuthenticated || user?.role !== 'admin') {
            navigate('/login');
        }
    }, [isAuthenticated, user, navigate]);

    // Fetch accounts and capacity
    const fetchAccounts = async () => {
        try {
            const response = await adminAPI.getAccounts();
            if (response.data.success) {
                setAccounts(response.data.accounts || []);
                setCapacity(response.data.systemCapacity);
                setError(null);
            }
        } catch (error) {
            console.error('Failed to fetch accounts:', error);
            setError(error.response?.data?.error || 'Failed to fetch accounts');
        }
    };

    // Fetch QR code for a session
    const fetchQRCode = useCallback(async (sessionId) => {
        try {
            const response = await adminAPI.getQrCode(sessionId);
            
            console.log(`🔍 QR Poll response:`, response.data);
            
            if (response.data.qr) {
                console.log(`✅ QR code received`);
                setQrCode(response.data.qr);
                setQrLoading(false);
                return true; // Keep polling (QR received, waiting for scan)
            } else if (response.data.status === 'ready') {
                console.log(`✅ Account is ready! Phone detected: ${response.data.phone}`);
                
                // If phone is null, something went wrong - keep polling
                if (!response.data.phone) {
                    console.warn(`⚠️ Client ready but phone is null, continuing to poll...`);
                    setQrLoading(false);
                    return true; // Keep polling
                }
                
                // Clear QR immediately
                setQrCode(null);
                setQrLoading(false);
                
                // Confirm the phone with backend
                try {
                    console.log(`📤 Confirming phone: ${response.data.phone}`);
                    const confirmResponse = await adminAPI.confirmPhone(sessionId, {
                        phone: response.data.phone
                    });
                    
                    console.log(`📱 Phone confirmation response:`, confirmResponse.data);
                    if (confirmResponse.data.isExistingAccount) {
                        console.log(`⚠️ Phone already existed! Reused existing account: ${confirmResponse.data.sessionId}`);
                        alert(`✅ Phone linked!\n\nThis phone was already registered.\nReusing existing account with preserved counters.\n\nStatus: ${confirmResponse.data.accountStatus}`);
                    } else {
                        console.log(`✅ New phone registered: ${response.data.phone}`);
                        alert(`✅ Phone confirmed!\n\nNew account created with phone: ${response.data.phone}`);
                    }
                    
                    // Close modal immediately after confirmation
                    setShowQRModal(false);
                    setQrSessionId(null);
                    setReconnectingAccountId(null);
                    setQrCode(null);
                    setQrLoading(false);
                    
                    // Refresh accounts after a brief delay
                    setTimeout(() => {
                        fetchAccounts();
                    }, 300);
                    
                } catch (err) {
                    console.error(`❌ Error confirming phone:`, err);
                    alert(`❌ Error confirming phone: ${err.response?.data?.error || err.message}`);
                }
                
                return false; // Stop polling
            } else if (response.data.status === 'waiting') {
                console.log(`⏳ Still waiting for QR code...`);
                setQrLoading(true);
                return true; // Keep polling
            }
            
            console.warn(`⚠️ Unknown response status:`, response.data);
            return true; // Keep polling on unknown response
        } catch (error) {
            console.error('❌ Failed to fetch QR code:', error);
            setQrLoading(false);
            return true; // Keep polling on error
        }
    }, []);

    // Poll for QR code after registration
    useEffect(() => {
        if (!qrSessionId || !showQRModal) return;

        let pollCount = 0;
        const pollQR = async () => {
            const hasQR = await fetchQRCode(qrSessionId);
            if (!hasQR) {
                // Stop polling if no QR or account ready
                return;
            }
            pollCount++;
        };

        // Start polling immediately
        pollQR();
        
        // Adaptive polling: faster at start (1s for first 5 polls = 5s), then slower (3s)
        const interval = setInterval(() => {
            pollCount++;
            pollQR();
        }, pollCount < 5 ? 1000 : 3000); // 1s for first 5 seconds, then 3s

        return () => clearInterval(interval);
    }, [qrSessionId, showQRModal, fetchQRCode]);

    // Fetch accounts ONLY on initial page load and when user manually refreshes
    useEffect(() => {
        // Fetch once on mount
        fetchAccounts();
        
        // NO auto-refresh at all - user must refresh page manually
        // While QR modal is open, fetchAccounts is blocked by showQRModal dependency
    }, []);

    // Block ALL account fetches while QR modal is open
    useEffect(() => {
        // This effect does nothing - it's just to prevent fetches while modal is open
        // by keeping showQRModal in the dependency array of other effects
    }, [showQRModal]);

    // Generate unique session ID
    const generateUniqueSessionId = () => {
        return `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    };

    // Add new account
    const handleAddAccount = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const uniqueSessionId = sessionId || generateUniqueSessionId();
            const response = await adminAPI.registerAccount({
                sessionId: uniqueSessionId
            });

            if (response.data.success) {
                // Show QR modal and start fetching QR code
                setQrSessionId(uniqueSessionId);
                setQrCode(null);
                setShowQRModal(true);
                
                // Start fetching QR code immediately
                await fetchQRCode(uniqueSessionId);
                
                setSessionId('');
                setShowAddModal(false);
                // Don't fetch accounts yet - wait for modal to close
            } else {
                alert(`❌ Error: ${response.data.error}`);
            }
        } catch (error) {
            alert(`❌ Failed: ${error.response?.data?.error || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Activate account
    const handleActivate = async (sessionId) => {
        try {
            const response = await adminAPI.activateAccount(sessionId);
            if (response.data.success) {
                setAccounts(prev => prev.map(acc => acc.sessionId === sessionId ? { ...acc, isActive: true } : acc));
                setSelectedAccount(prev => prev && prev.sessionId === sessionId ? { ...prev, isActive: true } : prev);
                setTimeout(fetchAccounts, 1000);
            } else {
                alert(`❌ Error: ${response.data.error}`);
            }
        } catch (error) {
            alert(`❌ Failed: ${error.response?.data?.error || error.message}`);
        }
    };

    // Deactivate account
    const handleDeactivate = async (sessionId) => {
        try {
            const response = await adminAPI.deactivateAccount(sessionId);
            if (response.data.success) {
                setAccounts(prev => prev.map(acc => acc.sessionId === sessionId ? { ...acc, isActive: false } : acc));
                setSelectedAccount(prev => prev && prev.sessionId === sessionId ? { ...prev, isActive: false } : prev);
                setTimeout(fetchAccounts, 1000);
            } else {
                alert(`❌ Error: ${response.data.error}`);
            }
        } catch (error) {
            alert(`❌ Failed: ${error.response?.data?.error || error.message}`);
        }
    };

    // Delete account
    const handleDelete = async (sessionId) => {
        console.log(`🗑️ Delete button clicked for account: ${sessionId}`);
        // Show custom confirmation modal instead of window.confirm
        setDeleteConfirmSessionId(sessionId);
        setShowDeleteConfirm(true);
    };

    // Confirm deletion
    const confirmDelete = async () => {
        const sessionId = deleteConfirmSessionId;
        console.log(`✅ User confirmed deletion for: ${sessionId}`);
        setShowDeleteConfirm(false);

        try {
            console.log(`   Sending DELETE request...`);
            setDeletingAccountId(sessionId);
            
            const response = await adminAPI.deleteAccount(sessionId);
            console.log(`   Response:`, response.data);
            
            if (response.data.success) {
                console.log(`✅ Account deleted successfully`);
                alert(`✅ Account "${sessionId}" deleted successfully!\n\nRemoving from list...`);
                
                // Refresh the account list immediately
                await fetchAccounts();
                
                console.log(`✅ Account list refreshed`);
                setDeletingAccountId(null);
            } else {
                console.error(`❌ Delete failed:`, response.data.error);
                alert(`❌ Error: ${response.data.error}`);
                setDeletingAccountId(null);
            }
        } catch (error) {
            console.error(`❌ Delete request failed:`, error);
            alert(`❌ Failed to delete account:\n${error.response?.data?.error || error.message}`);
            setDeletingAccountId(null);
        }
    };

    // Reconnect account (rescan QR code)
    const handleReconnectAccount = async (sessionId) => {
        try {
            console.log(`🔄 Starting reconnection for account: ${sessionId}`);
            setReconnectingAccountId(sessionId);
            setQrCode(null);
            setQrLoading(true);

            // Request new QR code for reconnection
            console.log(`📤 Calling /admin/accounts/${sessionId}/reconnect`);
            const response = await adminAPI.reconnectAccount(sessionId);
            
            console.log(`📥 Reconnection response:`, response.data);
            if (response.data.success) {
                setQrSessionId(sessionId);
                setShowQRModal(true);
                console.log('✅ Reconnection initiated, session:', sessionId);
            } else {
                console.error(`❌ Reconnection failed:`, response.data.error);
                alert(`❌ Error: ${response.data.error}`);
                setQrLoading(false);
            }
        } catch (error) {
            console.error('❌ Error reconnecting account:', error);
            setQrLoading(false);
            alert(`❌ Failed: ${error.response?.data?.error || error.message}`);
        }
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'ready': return '#4CAF50';
            case 'initializing': return '#FFC107';
            case 'disconnected': return '#F44336';
            case 'suspended': return '#9C27B0';
            case 'risky': return '#FF9800';
            default: return '#999';
        }
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'ready': return '✅';
            case 'initializing': return '⏳';
            case 'disconnected': return '❌';
            case 'suspended': return '🚫';
            case 'risky': return '⚠️';
            default: return '❓';
        }
    };

    return (
        <AdminLayout>
            <div className="min-h-screen bg-[#0B0E14] relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
                {/* Background Gradient */}
                <div className="fixed inset-0 bg-gradient-to-br from-[#128C7E]/5 via-transparent to-[#128C7E]/3 pointer-events-none" />

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                    
                    {/* Error Banner */}
                    {error && (
                        <div className="mb-4 sm:mb-6 bg-[#EF4444]/10 border border-[#EF4444] text-[#EF4444] px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl flex items-center gap-3 text-sm sm:text-base">
                            <AlertCircle size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
                            <span className="font-medium">{error}</span>
                        </div>
                    )}

                    {/* Header Section */}
                    <div className="mb-8 sm:mb-10 lg:mb-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
                        <div>
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-2 sm:mb-3">
                                📱 {t('WhatsApp Accounts')}
                            </h1>
                            <p className="text-[#A0AEC0] text-sm sm:text-lg">
                                {t('Manage multiple WhatsApp accounts for distributed OTP sending')}
                            </p>
                        </div>
                        <button 
                            className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-[#128C7E] to-[#0DA699] text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:shadow-lg hover:shadow-[#128C7E]/30 transition-all duration-300 font-semibold active:scale-95 whitespace-nowrap text-sm sm:text-base"
                            onClick={() => {
                                setSessionId(generateUniqueSessionId());
                                setShowAddModal(true);
                            }}
                        >
                            <Plus size={18} className="sm:w-5 sm:h-5" /> <span className="hidden sm:inline">{t('Add Account')}</span><span className="sm:hidden">Add</span>
                        </button>
                    </div>

                    {/* System Capacity Card */}
                    {capacity && (
                        <div className="mb-8 sm:mb-10 lg:mb-12 bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#2D3748] rounded-xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl">
                            <h3 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-8 flex items-center gap-3">
                                <Zap className="w-5 sm:w-7 h-5 sm:h-7 text-[#128C7E]" />
                                {t('System Capacity')}
                            </h3>
                            
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                                <div className="p-4 bg-[#0B0E14]/50 rounded-xl border border-[#2D3748]">
                                    <p className="text-[#A0AEC0] text-sm font-medium">{t('Active Accounts')}</p>
                                    <p className="text-3xl font-bold text-[#128C7E] mt-2">{capacity.accountsActive}/{capacity.accountsTotal}</p>
                                </div>
                                <div className="p-4 bg-[#0B0E14]/50 rounded-xl border border-[#2D3748]">
                                    <p className="text-[#A0AEC0] text-sm font-medium">{t('Daily Capacity')}</p>
                                    <p className="text-3xl font-bold text-[#128C7E] mt-2">{capacity.totalDailyCapacity}</p>
                                </div>
                                <div className="p-4 bg-[#0B0E14]/50 rounded-xl border border-[#2D3748]">
                                    <p className="text-[#A0AEC0] text-sm font-medium">{t('Daily Used')}</p>
                                    <p className="text-3xl font-bold text-[#F59E0B] mt-2">{capacity.totalDailyUsed}</p>
                                </div>
                                <div className="p-4 bg-[#0B0E14]/50 rounded-xl border border-[#2D3748]">
                                    <p className="text-[#A0AEC0] text-sm font-medium">{t('Utilization')}</p>
                                    <p className="text-3xl font-bold text-[#10B981] mt-2">{capacity.utilizationPercent}%</p>
                                </div>
                            </div>

                            {/* Capacity Progress Bar */}
                            <div className="w-full bg-[#0B0E14] rounded-full h-3">
                                <div 
                                    className="bg-gradient-to-r from-[#128C7E] to-[#0DA699] h-3 rounded-full transition-all duration-300"
                                    style={{ width: `${capacity.utilizationPercent}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Accounts Grid */}
                    <div className="space-y-8">
                        {/* Tabs */}
                        <div className="flex items-center gap-2 border-b border-[#2D3748]">
                            <button
                                onClick={() => setAccountsTab('all')}
                                className={`px-6 py-4 font-semibold transition-all border-b-2 ${
                                    accountsTab === 'all'
                                        ? 'border-[#128C7E] text-[#128C7E]'
                                        : 'border-transparent text-[#A0AEC0] hover:text-white'
                                }`}
                            >
                                📋 {t('All Accounts')} ({accounts.length})
                            </button>
                            <button
                                onClick={() => setAccountsTab('admin')}
                                className={`px-6 py-4 font-semibold transition-all border-b-2 ${
                                    accountsTab === 'admin'
                                        ? 'border-[#8B5CF6] text-[#A78BFA]'
                                        : 'border-transparent text-[#A0AEC0] hover:text-white'
                                }`}
                            >
                                👨‍💼 {t('Admin')} ({accounts.filter(a => a.isPublic).length})
                            </button>
                            <button
                                onClick={() => setAccountsTab('user')}
                                className={`px-6 py-4 font-semibold transition-all border-b-2 ${
                                    accountsTab === 'user'
                                        ? 'border-[#128C7E] text-[#5EEAD4]'
                                        : 'border-transparent text-[#A0AEC0] hover:text-white'
                                }`}
                            >
                                👤 {t('User')} ({accounts.filter(a => !a.isPublic).length})
                            </button>
                        </div>

                        {/* Account Cards Grid */}
                        {accounts.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {accounts
                                    .filter(acc => 
                                        accountsTab === 'all' ? true :
                                        accountsTab === 'admin' ? acc.isPublic :
                                        !acc.isPublic
                                    )
                                    .map((account, index) => (
                                        <CompactAccountCard
                                            key={`${account.sessionId}-${index}`}
                                            account={account}
                                            onViewDetails={() => {
                                                setSelectedAccount(account);
                                                setShowDetailsModal(true);
                                            }}
                                            getStatusColor={getStatusColor}
                                            t={t}
                                        />
                                    ))
                                }
                            </div>
                        ) : (
                            <div className="text-center py-20">
                                <div className="text-7xl mb-6">📭</div>
                                <h3 className="text-3xl font-bold text-white mb-3">{t('No accounts yet')}</h3>
                                <p className="text-[#A0AEC0] text-lg mb-8 max-w-md mx-auto">
                                    {t('Get started by adding your first WhatsApp account')}
                                </p>
                                <button 
                                    className="inline-flex items-center gap-2 bg-[#128C7E] text-white px-6 py-3 rounded-xl hover:bg-[#0DA699] transition-all font-semibold"
                                    onClick={() => {
                                        setSessionId(generateUniqueSessionId());
                                        setShowAddModal(true);
                                    }}
                                >
                                    <Plus size={20} /> {t('Add Your First Account')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Add Account Modal */}
                    {showAddModal && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <div className="bg-gradient-to-br from-[#1A1F2E] to-[#141820] border border-[#128C7E]/30 rounded-3xl max-w-lg w-full p-8 shadow-2xl shadow-[#128C7E]/20">
                                <h2 className="text-3xl font-bold text-white mb-6">➕ {t('Add New Account')}</h2>
                                <form onSubmit={handleAddAccount}>
                                    <div className="mb-6">
                                        <label className="block text-sm font-semibold text-[#A0AEC0] mb-2">
                                            {t('Session ID')}
                                        </label>
                                        <input 
                                            type="text" 
                                            value={sessionId}
                                            readOnly
                                            className="w-full bg-[#0B0E14] border border-[#2D3748] text-white px-4 py-3 rounded-lg cursor-not-allowed opacity-60"
                                        />
                                        <p className="text-[#718096] text-xs mt-2">{t('Auto-generated unique ID')}</p>
                                    </div>

                                    <div className="mb-8 p-4 bg-[#128C7E]/10 border border-[#128C7E]/30 rounded-xl">
                                        <p className="text-[#A0AEC0] text-sm">
                                            📲 {t('After registering, youll get a QR code to scan with WhatsApp. The phone number will be detected automatically.')}
                                        </p>
                                    </div>

                                    <div className="flex gap-4">
                                        <button 
                                            type="button" 
                                            className="flex-1 bg-[#2D3748] hover:bg-[#2D3748]/80 text-white px-6 py-3 rounded-lg transition-all font-semibold"
                                            onClick={() => setShowAddModal(false)}
                                        >
                                            {t('Cancel')}
                                        </button>
                                        <button 
                                            type="submit" 
                                            className="flex-1 bg-gradient-to-br from-[#128C7E] to-[#0DA699] hover:shadow-lg hover:shadow-[#128C7E]/30 text-white px-6 py-3 rounded-lg transition-all font-semibold active:scale-95"
                                            disabled={loading}
                                        >
                                            {loading ? `⏳ ${t('Adding')}...` : `✓ ${t('Add Account')}`}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* QR Code Modal */}
                    {showQRModal && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <div className="bg-gradient-to-br from-[#1A1F2E] to-[#141820] border border-[#128C7E]/30 rounded-3xl max-w-md w-full p-8 shadow-2xl shadow-[#128C7E]/20">
                                <div className="flex items-center justify-between mb-8">
                                    <h2 className="text-3xl font-bold text-white">📱 {t('Scan QR Code')}</h2>
                                    <button 
                                        className="text-[#A0AEC0] hover:text-white hover:bg-[#2D3748] p-2 rounded-lg transition-all"
                                        onClick={() => setShowQRModal(false)}
                                    >
                                        <X size={24} />
                                    </button>
                                </div>

                                {!qrCode && !qrLoading ? (
                                    <div className="text-center py-12">
                                        <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4 animate-pulse" />
                                        <p className="text-[#A0AEC0] text-lg font-semibold">✅ {t('Phone detected and confirmed!')}</p>
                                        <p className="text-[#718096] text-sm mt-2">{t('Closing modal...')}</p>
                                    </div>
                                ) : qrLoading && !qrCode ? (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 border-4 border-[#128C7E]/20 border-t-[#128C7E] rounded-full animate-spin mx-auto mb-4"></div>
                                        <p className="text-[#A0AEC0]">{t('Generating QR code...')}</p>
                                    </div>
                                ) : qrCode ? (
                                    <div className="space-y-6">
                                        <div className="flex justify-center">
                                            <div className="bg-white p-4 rounded-xl">
                                                <QRCodeSVG 
                                                    value={qrCode} 
                                                    size={280}
                                                    level="H"
                                                    includeMargin={true}
                                                />
                                            </div>
                                        </div>
                                        <div className="bg-[#0B0E14]/50 border border-[#2D3748] rounded-xl p-6">
                                            <h3 className="text-white font-bold mb-3">📋 {t('Steps')}:</h3>
                                            <ol className="text-[#A0AEC0] text-sm space-y-2">
                                                <li>1. {t('Open WhatsApp on your phone')}</li>
                                                <li>2. {t('Go to Settings → Linked Devices')}</li>
                                                <li>3. {t('Tap Link a Device')}</li>
                                                <li>4. {t('Point camera at QR code')}</li>
                                                <li>5. {t('Wait for confirmation')}</li>
                                            </ol>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <AlertCircle className="w-16 h-16 text-[#EF4444] mx-auto mb-4" />
                                        <p className="text-[#EF4444] font-semibold mb-4">❌ {t('Failed to generate QR code')}</p>
                                        <button 
                                            className="bg-[#128C7E] hover:bg-[#0DA699] text-white px-4 py-2 rounded-lg transition-all font-semibold"
                                            onClick={() => fetchQRCode(qrSessionId)}
                                        >
                                            🔄 {t('Retry')}
                                        </button>
                                    </div>
                                )}

                                <div className="mt-8 pt-6 border-t border-[#2D3748] text-center text-sm text-[#718096]">
                                    {t('Session')}: <strong className="text-[#A0AEC0]">{qrSessionId}</strong>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {showDeleteConfirm && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <div className="bg-gradient-to-br from-[#1A1F2E] to-[#141820] border border-[#EF4444]/30 rounded-3xl max-w-lg w-full p-8 shadow-2xl shadow-[#EF4444]/20">
                                <h2 className="text-3xl font-bold text-white mb-6">🗑️ {t('Delete Account')}</h2>
                                
                                <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-6 mb-8">
                                    <p className="text-white font-semibold mb-4">⚠️ {t('This action will')}:</p>
                                    <ul className="text-[#A0AEC0] text-sm space-y-2 mb-4">
                                        <li>✓ {t('Destroy the WhatsApp client')}</li>
                                        <li>✓ {t('Delete from database')}</li>
                                        <li>✓ {t('Free up system capacity')}</li>
                                    </ul>
                                    <p className="text-[#EF4444] font-bold">❌ {t('This action CANNOT be undone')}</p>
                                </div>

                                <div className="mb-4 p-4 bg-[#0B0E14]/50 rounded-lg border border-[#2D3748]">
                                    <p className="text-[#A0AEC0] text-sm"><strong>{t('Session ID')}:</strong> {deleteConfirmSessionId}</p>
                                </div>

                                <div className="flex gap-4">
                                    <button 
                                        type="button" 
                                        className="flex-1 bg-[#2D3748] hover:bg-[#2D3748]/80 text-white px-6 py-3 rounded-lg transition-all font-semibold"
                                        onClick={() => setShowDeleteConfirm(false)}
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="flex-1 bg-[#EF4444] hover:bg-[#EF4444]/90 text-white px-6 py-3 rounded-lg transition-all font-semibold active:scale-95"
                                        onClick={confirmDelete}
                                    >
                                        {t('Yes, Delete')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Account Details Modal */}
                    {showDetailsModal && selectedAccount && (
                        <AccountDetailsModal
                            account={selectedAccount}
                            onClose={() => {
                                setShowDetailsModal(false);
                                setSelectedAccount(null);
                            }}
                            onDelete={handleDelete}
                            onActivate={handleActivate}
                            onDeactivate={handleDeactivate}
                            onReconnect={handleReconnectAccount}
                            isDeleting={deletingAccountId === selectedAccount.sessionId}
                            isReconnecting={qrLoading && reconnectingAccountId === selectedAccount.sessionId}
                            getStatusColor={getStatusColor}
                            getStatusIcon={getStatusIcon}
                            t={t}
                        />
                    )}

                </div>
            </div>
        </AdminLayout>
    );
};

// Compact Account Card - Click to View Details
function CompactAccountCard({ account, onViewDetails, getStatusColor, t }) {
    return (
        <button
            onClick={onViewDetails}
            className={`group h-full text-left bg-gradient-to-br backdrop-blur-xl border rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1 active:translate-y-0 ${
                account.isPublic
                    ? 'from-[#1A1F2E]/80 to-[#161B26]/80 border-[#2D3748] hover:border-[#8B5CF6]/60 hover:shadow-[#8B5CF6]/20'
                    : 'from-[#1A1F2E]/80 to-[#16213E]/80 border-[#2D3748] hover:border-[#128C7E]/60 hover:shadow-[#128C7E]/20'
            }`}
        >
            {/* Status Bar */}
            <div 
                className="h-1 w-full"
                style={{ backgroundColor: getStatusColor(account.status) }}
            ></div>

            {/* Content */}
            <div className="p-4 space-y-3">
                {/* Header */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${
                            account.isPublic
                                ? 'bg-[#8B5CF6]/20 text-[#A78BFA]'
                                : 'bg-[#128C7E]/20 text-[#5EEAD4]'
                        }`}>
                            {account.isPublic ? '👨‍💼' : '👤'}
                        </span>
                        <span className="text-xs font-bold uppercase" style={{ color: getStatusColor(account.status) }}>
                            {account.status}
                        </span>
                    </div>
                    <p className="text-xs font-mono text-[#A0AEC0] truncate">{account.sessionId}</p>
                </div>

                {/* Phone */}
                {account.phone && (
                    <div className="text-sm">
                        <p className="text-xs text-[#718096] mb-1">📱 Phone</p>
                        <p className="text-white font-semibold font-mono">{account.phone}</p>
                    </div>
                )}

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                        <p className="text-[#718096]">Sent</p>
                        <p className="text-white font-bold">{account.totalSent}</p>
                    </div>
                    <div>
                        <p className={account.failureRate > 5 ? 'text-[#EF4444]' : 'text-[#10B981]'}>
                            Fail Rate
                        </p>
                        <p className={`font-bold ${account.failureRate > 5 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                            {account.failureRate}%
                        </p>
                    </div>
                </div>

                {/* View Details Link */}
                <div className="pt-2 border-t border-[#2D3748]/60 flex items-center justify-between">
                    <span className="text-xs text-[#718096]">📋 View Details</span>
                    <span className="text-lg group-hover:translate-x-1 transition-transform">→</span>
                </div>
            </div>
        </button>
    );
}

// Account Details Modal
function AccountDetailsModal({ 
    account, onClose, onDelete, onActivate, onDeactivate, onReconnect, 
    isDeleting, isReconnecting, getStatusColor, getStatusIcon, t 
}) {
    const dailyPercent = (parseInt(account.dailyUsage.split('/')[0]) / parseInt(account.dailyUsage.split('/')[1])) * 100;
    const hourlyPercent = (parseInt(account.hourlyUsage.split('/')[0]) / parseInt(account.hourlyUsage.split('/')[1])) * 100;
    const minutePercent = (parseInt(account.minuteUsage.split('/')[0]) / parseInt(account.minuteUsage.split('/')[1])) * 100;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className={`bg-gradient-to-br backdrop-blur-2xl border rounded-xl w-full max-w-3xl shadow-2xl my-4 ${
                account.isPublic
                    ? 'from-[#1A1F2E]/95 to-[#161B26]/95 border-[#8B5CF6]/30'
                    : 'from-[#1A1F2E]/95 to-[#16213E]/95 border-[#128C7E]/30'
            }`}>
                
                {/* Header */}
                <div className="px-6 py-4 border-b border-[#2D3748]/60 flex items-start justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                                account.isPublic
                                    ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/30'
                                    : 'bg-[#128C7E]/20 text-[#5EEAD4] border border-[#128C7E]/30'
                            }`}>
                                {account.isPublic ? '👨‍💼 Admin' : '👤 User'}
                            </span>
                            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: getStatusColor(account.status) }}>
                                {getStatusIcon(account.status)} {account.status}
                            </span>
                        </div>
                        <h2 className="text-lg font-mono font-bold text-white truncate">{account.sessionId}</h2>
                    </div>
                    <button 
                        className="text-[#A0AEC0] hover:text-white hover:bg-[#2D3748] p-1 rounded transition-all flex-shrink-0"
                        onClick={onClose}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content - 3 Column Grid */}
                <div className="px-6 py-5">
                    <div className="grid grid-cols-3 gap-4">
                        {/* Left Column */}
                        <div className="space-y-3">
                            {/* Phone */}
                            {account.phone && (
                                <div className={`rounded-lg p-4 border ${
                                    account.isPublic
                                        ? 'bg-[#8B5CF6]/10 border-[#8B5CF6]/30'
                                        : 'bg-[#128C7E]/10 border-[#128C7E]/30'
                                }`}>
                                    <p className="text-[#A0AEC0] text-xs font-semibold mb-1">📱 Phone</p>
                                    <p className="text-sm font-bold text-white font-mono break-all">{account.phone}</p>
                                </div>
                            )}

                            {/* Total Sent */}
                            <div className="bg-[#0B0E14]/50 rounded-lg p-4 border border-[#2D3748]/60">
                                <p className="text-[#718096] text-xs font-semibold mb-1">📤 Total</p>
                                <p className="text-2xl font-bold text-white">{account.totalSent}</p>
                            </div>

                            {/* Failure Rate */}
                            <div className={`rounded-lg p-4 border ${account.failureRate > 5 ? 'bg-[#EF4444]/10 border-[#EF4444]/30' : 'bg-[#10B981]/10 border-[#10B981]/30'}`}>
                                <p className="text-[#718096] text-xs font-semibold mb-1">📊 Failure</p>
                                <p className={`text-2xl font-bold ${account.failureRate > 5 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{account.failureRate}%</p>
                            </div>

                            {/* Last Activity */}
                            <div className="bg-[#0B0E14]/50 rounded-lg p-4 border border-[#2D3748]/60">
                                <p className="text-[#718096] text-xs font-semibold mb-1">🕐 Last Used</p>
                                <p className="text-xs text-white font-mono">{account.lastUsed === 'Never' ? t('Never') : new Date(account.lastUsed).toLocaleDateString()}</p>
                            </div>
                        </div>

                        {/* Middle Column - Rate Limits */}
                        <div className="space-y-3">
                            <div className="text-sm font-bold text-white">⚡ Rate Limits</div>

                            {/* Daily */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[#A0AEC0] text-xs font-semibold">📅 Daily</span>
                                    <span className="text-white text-xs font-bold">{account.dailyUsage}</span>
                                </div>
                                <div className="w-full bg-[#0B0E14]/70 rounded-full h-3 overflow-hidden border border-[#2D3748]/40">
                                    <div 
                                        className={`h-full transition-all duration-500 ${
                                            dailyPercent > 80 ? 'bg-gradient-to-r from-[#EF4444] to-[#DC2626]' :
                                            dailyPercent > 50 ? 'bg-gradient-to-r from-[#F59E0B] to-[#D97706]' :
                                            account.isPublic ? 'bg-gradient-to-r from-[#A78BFA] to-[#8B5CF6]' : 'bg-gradient-to-r from-[#128C7E] to-[#0DA699]'
                                        }`}
                                        style={{ width: `${Math.min(dailyPercent, 100)}%` }}
                                    ></div>
                                </div>
                                <p className="text-[#718096] text-xs mt-1">{Math.round(dailyPercent)}%</p>
                            </div>

                            {/* Hourly */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[#A0AEC0] text-xs font-semibold">⏱️ Hourly</span>
                                    <span className="text-white text-xs font-bold">{account.hourlyUsage}</span>
                                </div>
                                <div className="w-full bg-[#0B0E14]/70 rounded-full h-3 overflow-hidden border border-[#2D3748]/40">
                                    <div 
                                        className={`h-full transition-all duration-500 ${
                                            hourlyPercent > 80 ? 'bg-gradient-to-r from-[#EF4444] to-[#DC2626]' :
                                            hourlyPercent > 50 ? 'bg-gradient-to-r from-[#F59E0B] to-[#D97706]' :
                                            account.isPublic ? 'bg-gradient-to-r from-[#6366F1] to-[#4F46E5]' : 'bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8]'
                                        }`}
                                        style={{ width: `${Math.min(hourlyPercent, 100)}%` }}
                                    ></div>
                                </div>
                                <p className="text-[#718096] text-xs mt-1">{Math.round(hourlyPercent)}%</p>
                            </div>

                            {/* Per Minute */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[#A0AEC0] text-xs font-semibold">⚡ Per Min</span>
                                    <span className="text-white text-xs font-bold">{account.minuteUsage}</span>
                                </div>
                                <div className="w-full bg-[#0B0E14]/70 rounded-full h-3 overflow-hidden border border-[#2D3748]/40">
                                    <div 
                                        className={`h-full transition-all duration-500 ${
                                            minutePercent > 80 ? 'bg-gradient-to-r from-[#EF4444] to-[#DC2626]' :
                                            minutePercent > 50 ? 'bg-gradient-to-r from-[#F59E0B] to-[#D97706]' :
                                            account.isPublic ? 'bg-gradient-to-r from-[#EC4899] to-[#BE185D]' : 'bg-gradient-to-r from-[#EC4899] to-[#DB2777]'
                                        }`}
                                        style={{ width: `${Math.min(minutePercent, 100)}%` }}
                                    ></div>
                                </div>
                                <p className="text-[#718096] text-xs mt-1">{Math.round(minutePercent)}%</p>
                            </div>
                        </div>

                        {/* Right Column - Status */}
                        <div className="space-y-3">
                            <div className="text-sm font-bold text-white">⚙️ Status</div>

                            {/* Status Badge */}
                            <div className={`rounded-lg p-4 border text-center ${
                                account.isPublic
                                    ? 'bg-[#8B5CF6]/10 border-[#8B5CF6]/30'
                                    : 'bg-[#128C7E]/10 border-[#128C7E]/30'
                            }`}>
                                <p className="text-3xl mb-2">{getStatusIcon(account.status)}</p>
                                <p className="text-sm font-bold text-white capitalize">{account.status}</p>
                                <p className={`text-xs mt-1 ${account.isActive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                                    {account.isActive ? '✅ Active' : '⛔ Inactive'}
                                </p>
                            </div>

                            {/* Account Type */}
                            <div className="bg-[#0B0E14]/50 rounded-lg p-4 border border-[#2D3748]/60">
                                <p className="text-[#718096] text-xs font-semibold mb-2">📌 Type</p>
                                <p className="text-xs text-white font-bold">{account.isPublic ? 'Admin Created' : 'User Created'}</p>
                            </div>

                            {/* Quick Actions */}
                            <div className="bg-gradient-to-br from-[#8B5CF6]/5 to-[#128C7E]/5 rounded-lg p-3 border border-[#2D3748]/40">
                                <p className="text-[#A0AEC0] text-xs font-semibold mb-2">🎯 Actions</p>
                                <div className="space-y-1 text-xs">
                                    <div className="flex items-center gap-1 text-[#A0AEC0]">
                                        <QrCode size={12} /> QR
                                    </div>
                                    <div className={`flex items-center gap-1 ${account.isActive ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                                        {account.isActive ? <PowerOff size={12} /> : <Power size={12} />}
                                        {account.isActive ? 'Off' : 'On'}
                                    </div>
                                    <div className="flex items-center gap-1 text-[#EF4444]">
                                        <Trash2 size={12} /> Delete
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-[#2D3748]/60 grid grid-cols-2 gap-2">
                    <button 
                        className={`flex items-center justify-center gap-1 border px-3 py-2 rounded-lg transition-all font-semibold text-sm ${
                            account.isPublic
                                ? 'bg-[#8B5CF6]/20 hover:bg-[#8B5CF6]/40 border-[#8B5CF6]/50 text-[#A78BFA]'
                                : 'bg-[#128C7E]/20 hover:bg-[#128C7E]/40 border-[#128C7E]/50 text-[#128C7E]'
                        }`}
                        onClick={() => onReconnect(account.sessionId)}
                        disabled={isReconnecting}
                    >
                        <QrCode size={14} />
                        {isReconnecting ? 'Scanning...' : 'QR'}
                    </button>

                    {account.isActive ? (
                        <button 
                            className="flex items-center justify-center gap-1 bg-[#EF4444]/15 hover:bg-[#EF4444]/25 border border-[#EF4444]/50 text-[#EF4444] px-3 py-2 rounded-lg transition-all font-semibold text-sm"
                            onClick={() => onDeactivate(account.sessionId)}
                        >
                            <PowerOff size={14} />
                            Off
                        </button>
                    ) : (
                        <button 
                            className="flex items-center justify-center gap-1 bg-[#10B981]/15 hover:bg-[#10B981]/25 border border-[#10B981]/50 text-[#10B981] px-3 py-2 rounded-lg transition-all font-semibold text-sm"
                            onClick={() => onActivate(account.sessionId)}
                        >
                            <Power size={14} />
                            On
                        </button>
                    )}
                </div>

                {/* Delete Row */}
                <div className="px-6 py-3 border-t border-[#2D3748]/60 flex gap-2">
                    <button 
                        className="flex-1 flex items-center justify-center gap-1 bg-[#EF4444]/15 hover:bg-[#EF4444]/25 border border-[#EF4444]/50 text-[#EF4444] px-3 py-2 rounded-lg transition-all font-semibold text-sm"
                        onClick={() => onDelete(account.sessionId)}
                        disabled={isDeleting}
                    >
                        <Trash2 size={14} />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>

                    <button 
                        className="flex-1 bg-[#2D3748] hover:bg-[#2D3748]/80 text-white px-3 py-2 rounded-lg transition-all font-semibold text-sm"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AdminAccounts;
